/** Page-aware copilot — interpret live layout state for the assistant. */

import { getPageHelp } from "assistant/knowledge";

const CATEGORY_LABELS = {
  fr: {
    1: "Inscrites",
    2: "Sans recruteur",
    3: "Affectées",
    4: "Affectées avec présence",
  },
  en: {
    1: "Enrolled",
    2: "Without recruiter",
    3: "Assigned",
    4: "Assigned with attendance",
  },
};

function labelFor(category, language) {
  const map = CATEGORY_LABELS[language] || CATEGORY_LABELS.fr;
  return map[category] || String(category);
}

export function buildPageAwareContext(pathname, pageContext, language) {
  const help = getPageHelp(pathname, language);
  if (!pageContext && !help) return null;

  const fr = language !== "en";
  const key = pageContext?.pageKey;
  const filters = pageContext?.filters || {};
  const selection = pageContext?.selection || null;
  const counts = pageContext?.counts || {};

  const lines = [];
  const suggestions = [];
  const actions = [];

  if (help) {
    lines.push(fr ? `Page : ${help.title}` : `Page: ${help.title}`);
  }

  if (key === "enseignantes") {
    if (filters.category != null) {
      lines.push(
        fr
          ? `Filtre pipeline : ${labelFor(filters.category, language)} (${filters.totalResults ?? "—"} résultat(s), page ${filters.page ?? 1})`
          : `Pipeline filter: ${labelFor(filters.category, language)} (${filters.totalResults ?? "—"} result(s), page ${filters.page ?? 1})`
      );
    }
    if (selection?.teacherId) {
      lines.push(fr ? `Ligne sélectionnée : ${selection.name} (${selection.regNumber})` : `Selected row: ${selection.name} (${selection.regNumber})`);
      suggestions.push(fr ? `Fiche complète de ${selection.name}` : `Full story for ${selection.name}`);
      actions.push({ id: "profile-selected", route: `/enseignant/${selection.teacherId}`, label: fr ? "Fiche" : "Profile" });
      actions.push({ id: "story-selected", route: `/histoire-des-enseignants/${selection.teacherId}`, label: fr ? "Historique" : "History" });
    } else {
      suggestions.push(fr ? "Chercher enseignant Hamza" : "Search teacher named Hamza");
    }
    suggestions.push(fr ? "Combien d'enseignantes inscrites ?" : "How many enrolled teachers?");
  }

  if (key === "affectations") {
    const geo = [filters.province, filters.department, filters.subprefecture, filters.establishment].filter(Boolean);
    if (geo.length) {
      lines.push(fr ? `Filtres liste : ${geo.join(" → ")}` : `List filters: ${geo.join(" → ")}`);
    }
    if (filters.appliedListFilters?.search) {
      lines.push(fr ? `Recherche : « ${filters.appliedListFilters.search} »` : `Search: “${filters.appliedListFilters.search}”`);
    }
    lines.push(
      fr
        ? `Tableau : ${filters.totalRecords ?? "—"} affectation(s), page ${filters.page ?? 1}`
        : `Table: ${filters.totalRecords ?? "—"} assignment(s), page ${filters.page ?? 1}`
    );
    suggestions.push(fr ? "Guide affectation" : "Assignment guide");
  }

  if (key === "leave-requests") {
    lines.push(
      fr
        ? `Liste : ${filters.total ?? "—"} demande(s), ${filters.pending ?? 0} en attente`
        : `List: ${filters.total ?? "—"} request(s), ${filters.pending ?? 0} pending`
    );
    suggestions.push(fr ? "Congés en attente" : "Pending leave");
  }

  if (key === "area-attendance") {
    if (filters.lastQuery) {
      lines.push(
        fr
          ? `Requête active : ${filters.startDate} → ${filters.endDate}, min. ${filters.days} jour(s) — ${filters.checked ?? 0}/${filters.assigned ?? 0} vérifiées`
          : `Active query: ${filters.startDate} → ${filters.endDate}, min. ${filters.days} day(s) — ${filters.checked ?? 0}/${filters.assigned ?? 0} checked`
      );
    } else {
      lines.push(
        fr
          ? `Filtres : ${filters.startDate} → ${filters.endDate}, min. ${filters.days} jour(s) (cliquez Rechercher sur la page)`
          : `Filters: ${filters.startDate} → ${filters.endDate}, min. ${filters.days} day(s) (click Search on page)`
      );
    }
    suggestions.push(fr ? "Synthèse présence zone" : "Area attendance summary");
  }

  if (key === "establishment-attendance") {
    const geo = [filters.province, filters.department, filters.subprefecture, filters.establishmentLabel || filters.establishment].filter(Boolean);
    if (geo.length) {
      lines.push(fr ? `Zone : ${geo.join(" → ")}` : `Area: ${geo.join(" → ")}`);
    }
    lines.push(
      fr
        ? `Période ${filters.startDate} → ${filters.endDate}, rayon ${filters.radius} m — ${counts.teachers ?? 0} enseignante(s) chargée(s)`
        : `Period ${filters.startDate} → ${filters.endDate}, radius ${filters.radius}m — ${counts.teachers ?? 0} teacher(s) loaded`
    );
    suggestions.push(fr ? "Rapport présence établissement" : "Establishment attendance report");
  }

  if (key === "teacher-history" && selection?.teacherId) {
    lines.push(fr ? `Enseignante #${selection.teacherId}` : `Teacher #${selection.teacherId}`);
    if (selection.name) lines.push(`${selection.name}${selection.regNumber ? ` (${selection.regNumber})` : ""}`);
    suggestions.push(
      fr ? `Est-ce que ${selection.name || "cette enseignante"} est affectée et en congé ?` : `Is ${selection.name || "this teacher"} assigned and on leave?`
    );
  }

  const summary = lines.filter(Boolean).join("\n");
  return {
    pageKey: key,
    summary,
    suggestions: [...new Set(suggestions)].slice(0, 4),
    actions,
    selection,
    filters,
    help,
  };
}

export function formatPageAwareForLlm(pageAware, language) {
  if (!pageAware?.summary) return "";
  const fr = language !== "en";
  return fr
    ? `CONTEXTE ÉCRAN ACTUEL (filtres UI réels — ne pas inventer):\n${pageAware.summary}`
    : `CURRENT SCREEN CONTEXT (real UI filters — do not invent):\n${pageAware.summary}`;
}
